import apiClient, { getApiErrorInfo } from './api';

export interface MemoryMigrationResult {
  message: string;
  copiedCount: number;
  sourceReasoningEngineId: string;
  targetReasoningEngineId: string;
  skipDuplicates: boolean;
}

export interface MemoryMigrationPreview {
  sourceReasoningEngineId: string;
  targetReasoningEngineId: string;
  skipDuplicates: boolean;
  sourceFactCount: number;
  targetFactCount: number;
  sourceUserCount: number;
  sourceUsers: string[];
  previewSample: Array<{ userId: string | null; fact: string }>;
  message: string;
}

export interface MemoryMigrationRequest {
  sourceReasoningEngineId: string;
  targetReasoningEngineId: string;
  skipDuplicates?: boolean;
}

export type IntegrationHealthStatus = 'healthy' | 'unhealthy' | 'unconfigured';

export interface IntegrationHealth {
  key: string;
  name: string;
  description: string;
  status: IntegrationHealthStatus;
  latencyMs: number | null;
  message: string | null;
  checkedAt: string;
}

class AdminService {
  async getIntegrationsHealth(): Promise<IntegrationHealth[]> {
    try {
      const response = await apiClient.get<IntegrationHealth[]>('/api/admin/integrations/health');
      return response.data;
    } catch (error: unknown) {
      const apiError = getApiErrorInfo(error);
      throw new Error(apiError.message || 'Failed to check integrations health', { cause: error });
    }
  }

  async migrateAllMemories(request: MemoryMigrationRequest): Promise<MemoryMigrationResult> {
    try {
      const response = await apiClient.post<MemoryMigrationResult>('/api/admin/memories/migrate-all', {
        sourceReasoningEngineId: request.sourceReasoningEngineId,
        targetReasoningEngineId: request.targetReasoningEngineId,
        skipDuplicates: request.skipDuplicates ?? true,
      });

      return response.data;
    } catch (error: unknown) {
      const apiError = getApiErrorInfo(error);
      throw new Error(apiError.message || 'Failed to migrate memories');
    }
  }

  async previewMemoryMigration(request: MemoryMigrationRequest): Promise<MemoryMigrationPreview> {
    try {
      const response = await apiClient.post<MemoryMigrationPreview>('/api/admin/memories/migrate-all/preview', {
        sourceReasoningEngineId: request.sourceReasoningEngineId,
        targetReasoningEngineId: request.targetReasoningEngineId,
        skipDuplicates: request.skipDuplicates ?? true,
      });

      return response.data;
    } catch (error: unknown) {
      const apiError = getApiErrorInfo(error);
      throw new Error(apiError.message || 'Failed to preview memory migration');
    }
  }
}

export default new AdminService();